import { FileDropzone } from "@/components/FileDropzone";
import { toaster } from "@/components/ui/toaster";
import { SubscriptionGuestBatch } from "@/types/types";
import {
  Box,
  Button,
  Dialog,
  Grid,
  GridItem,
  HStack,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertCircle, CheckCircle, Download } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";

export default function UploadFileModal({
  isOpen,
  onClose,
  guestBatch,
  uploadFile,
  resetBatch,
}: {
  isOpen: boolean;
  onClose: () => void;
  guestBatch: SubscriptionGuestBatch | null;
  uploadFile: (file: File) => Promise<string>;
  resetBatch: () => void;
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const activeReferenceRef = useRef<string | null>(null);

  const isCurrentBatch =
    !guestBatch ||
    !activeReferenceRef.current ||
    guestBatch.reference === activeReferenceRef.current;

  const isComplete = isCurrentBatch && guestBatch?.status === "complete";
  const isError = isCurrentBatch && guestBatch?.status === "error";
  const hasErrors = (guestBatch?.errors?.length ?? 0) > 0;
  const isProcessing =
    isSubmitting ||
    (isCurrentBatch && guestBatch?.status === "processing");
  const showSuccess = !isProcessing && isComplete && !hasErrors;
  const showErrorResult =
    !isProcessing && ((isComplete && hasErrors) || isError);
  const showResult = showSuccess || showErrorResult;

  useEffect(() => {
    if (!isSubmitting || !isCurrentBatch) return;
    if (isComplete || isError) setIsSubmitting(false);
  }, [isSubmitting, isComplete, isError, isCurrentBatch]);

  const handleClose = () => {
    if (!isProcessing) {
      resetBatch();
      setSelectedFile(null);
      setIsSubmitting(false);
      setIsUploadingFile(false);
      activeReferenceRef.current = null;
    }
    onClose();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    resetBatch();
    activeReferenceRef.current = null;
    setIsSubmitting(true);
    setIsUploadingFile(true);
    try {
      activeReferenceRef.current = await uploadFile(selectedFile);
    } catch {
      setIsSubmitting(false);
      setIsUploadingFile(false);
      activeReferenceRef.current = null;
      toaster.create({
        title: "Error",
        description: "Hubo un error al subir el archivo de invitados.",
        type: "error",
        duration: 5000,
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  return (
    <Dialog.Root
      lazyMount
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="lg"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxHeight="80vh"
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
              <HStack justify="space-between" align="center" w="full" pr={8}>
                <Dialog.Title fontSize="lg" fontWeight="semibold">
                  Agregar invitados
                </Dialog.Title>
                {!isProcessing && !showResult && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = "/invitados_plantilla.xlsx";
                      link.download = "invitados_plantilla.xlsx";
                      link.click();
                    }}
                  >
                    <Download size={16} />
                    Descargar plantilla
                  </Button>
                )}
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body flex="1" overflowY="auto" py={6}>
              {isProcessing ? (
                <VStack gap={4} py={8}>
                  <Spinner size="lg" color="palomma.500" />
                  <Text fontWeight="medium" color="gray.700">
                    {isUploadingFile
                      ? "Subiendo archivo..."
                      : "Procesando archivo..."}
                  </Text>
                  {guestBatch?.totalCount != null ? (
                    <Text fontSize="sm" color="gray.500">
                      {guestBatch.successCount ?? 0} de {guestBatch.totalCount}{" "}
                      invitados procesados
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Esto puede tomar unos segundos
                    </Text>
                  )}
                </VStack>
              ) : showSuccess ? (
                <VStack gap={4} align="stretch" py={2}>
                  <VStack gap={3}>
                    <Box p={4} bg="green.50" borderRadius="full">
                      <CheckCircle size={40} color="#38a169" />
                    </Box>
                    <Text fontWeight="medium" color="green.600">
                      Todos los invitados se han agregado exitosamente
                    </Text>
                  </VStack>
                  <HStack gap={6} px={1}>
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Total de registros:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {guestBatch?.totalCount ?? 0}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Creados:
                      </Text>
                      <Text fontSize="sm" color="green.600">
                        {guestBatch?.successCount ?? 0}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              ) : showErrorResult ? (
                <VStack gap={4} align="stretch">
                  <VStack gap={3} py={2}>
                    <Box p={4} bg="red.50" borderRadius="full">
                      <AlertCircle size={40} color="#e53e3e" />
                    </Box>
                    <Text fontWeight="medium" color="red.600">
                      {isError
                        ? "Error al procesar el archivo"
                        : "El archivo se procesó con errores"}
                    </Text>
                  </VStack>

                  {guestBatch?.batchError && (
                    <Box w="full" p={4} bg="red.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.700">
                        {guestBatch.batchError}
                      </Text>
                    </Box>
                  )}

                  {isComplete && (
                    <HStack gap={6} px={1}>
                      <HStack>
                        <Text fontSize="sm" fontWeight={600}>
                          Creados:
                        </Text>
                        <Text fontSize="sm" color="green.600">
                          {guestBatch?.successCount ?? 0}
                        </Text>
                      </HStack>
                      <HStack>
                        <Text fontSize="sm" fontWeight={600}>
                          Errores:
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          {guestBatch?.errors?.length ?? 0}
                        </Text>
                      </HStack>
                    </HStack>
                  )}

                  {hasErrors && (
                    <Box w="full" overflowY="auto" maxHeight="280px">
                      <Grid templateColumns="repeat(2, 1fr)">
                        <GridItem
                          fontWeight="bold"
                          fontSize="sm"
                          borderBottom="1px solid #ebecef80"
                          p="0.5rem"
                          bg="#F7F9FC"
                        >
                          Número de documento
                        </GridItem>
                        <GridItem
                          fontWeight="bold"
                          fontSize="sm"
                          borderBottom="1px solid #ebecef80"
                          p="0.5rem"
                          bg="#F7F9FC"
                        >
                          Error
                        </GridItem>
                        {guestBatch?.errors?.map((error, idx) => (
                          <Fragment key={idx}>
                            <GridItem
                              fontSize="sm"
                              borderBottom="1px solid #ebecef80"
                              p="0.5rem"
                            >
                              {error.documentNumber}
                            </GridItem>
                            <GridItem
                              fontSize="sm"
                              color="red.700"
                              borderBottom="1px solid #ebecef80"
                              p="0.5rem"
                            >
                              {error.errorMessage}
                            </GridItem>
                          </Fragment>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </VStack>
              ) : (
                <FileDropzone
                  file={selectedFile}
                  onChange={setSelectedFile}
                  accept=".xlsx,.xls"
                  hint="Soporta archivos .XLSX y .XLS"
                />
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={isProcessing}
              >
                {showResult ? "Cerrar" : "Cancelar"}
              </Button>
              {!isProcessing && !showResult && (
                <Button
                  colorPalette="palomma"
                  onClick={handleUpload}
                  loading={isSubmitting}
                  disabled={!selectedFile}
                >
                  Agregar invitados
                </Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
